import clsx from 'clsx';
import { CartesianGrid, ComposedChart, Line, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useI18n, type Locale, type TranslationKey } from '../lib/i18n';
import { formatDate, formatNumber } from '../lib/format';
import { dayTone, type SeriesPoint, type SeriesRange, type UrlSeries } from '../lib/monitoring';
// The design tokens themselves, as the Tailwind preset reads them: the admin loads no token stylesheet.
import tokens from '../../../../packages/design-tokens/design-tokens.json';

/**
 * A URL's charts (operator's spec, 2026-10-20: "Response Time Charts" and each
 * site's history). One series, so no legend box for the line — the card's title
 * names it; failed checks are a second mark, keyed beside it. The line is
 * Sentinel's chart violet, which clears 3:1 on both themes' cards; failures wear
 * the error status, always with words in the key and the tooltip.
 */

/** Sentinel's chart violet — the categorical scale's fourth. */
const LINE = tokens.color.chart.categorical[3];
const FAILED = tokens.color.semantic.error.base;

const LOCALE_TAG: Record<Locale, string> = { en: 'en-GB', ar: 'ar-EG-u-nu-latn' };

function time(t: number, range: SeriesRange, locale: Locale): string {
  const options: Intl.DateTimeFormatOptions =
    range === '24h' ? { hour: '2-digit', minute: '2-digit' } : range === '7d' ? { weekday: 'short', day: 'numeric' } : { day: 'numeric', month: 'short' };
  return new Intl.DateTimeFormat(LOCALE_TAG[locale], options).format(new Date(t));
}

/** Ticks on round times: every 3 hours, every day, every 5 or every 15 days. */
function ticksFor(from: number, to: number, range: SeriesRange): number[] {
  const hour = 3_600_000;
  const day = 24 * hour;
  const step = range === '24h' ? 3 * hour : range === '7d' ? day : range === '30d' ? 5 * day : 15 * day;
  const first = new Date(from);
  if (range === '24h') first.setMinutes(0, 0, 0);
  else first.setHours(0, 0, 0, 0);
  const ticks: number[] = [];
  for (let t = first.getTime(); t <= to; t += step) if (t >= from) ticks.push(t);
  return ticks;
}

interface Row extends SeriesPoint {
  t: number;
}

export function ResponseTimeChart({ series, range }: { series: UrlSeries; range: SeriesRange }) {
  const { t, locale } = useI18n();
  const rows: Row[] = series.points.map((p) => ({ ...p, t: series.daily ? Date.parse(`${p.at}T00:00:00`) : Date.parse(p.at) }));
  const width = series.daily ? 86_400_000 : series.bucketMs;
  if (!rows.some((r) => r.checks > 0)) {
    return <p className="py-10 text-center text-body-sm text-muted">{t('monitoring.chart.noChecks')}</p>;
  }
  const from = rows[0].t;
  const to = rows[rows.length - 1].t + width;
  const anyFailed = rows.some((r) => r.down > 0);
  // The crosshair finds a slot whether or not it has a response to plot, so the tooltip finds its row by time.
  const byTime = new Map(rows.map((r) => [r.t, r]));
  const ms = (n: number) => t('monitoring.url.ms', { n: formatNumber(n, locale) });

  return (
    <div>
      <div className="h-64" dir="ltr">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
            <CartesianGrid stroke="var(--line)" vertical={false} />
            {rows
              .filter((r) => r.down > 0)
              .map((r) => (
                <ReferenceArea key={r.t} x1={r.t} x2={r.t + width} fill={FAILED} fillOpacity={0.14} strokeOpacity={0} ifOverflow="hidden" />
              ))}
            <XAxis
              dataKey="t"
              type="number"
              scale="time"
              domain={[from, to]}
              ticks={ticksFor(from, to, range)}
              tickFormatter={(value: number) => time(value, range, locale)}
              tick={{ fill: 'var(--text-tertiary)', fontSize: 11 }}
              axisLine={{ stroke: 'var(--line)' }}
              tickLine={false}
            />
            <YAxis
              width={56}
              tick={{ fill: 'var(--text-tertiary)', fontSize: 11 }}
              tickFormatter={(value: number) => (value >= 1000 ? `${Math.round(value / 100) / 10}k` : String(value))}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip
              // Shown for an empty slot too: "not checked in this time" is an answer.
              filterNull={false}
              cursor={{ stroke: 'var(--text-tertiary)', strokeWidth: 1 }}
              content={({ active, payload, label }) => {
                const row = active ? ((payload?.[0]?.payload as Row | undefined) ?? byTime.get(Number(label))) : undefined;
                if (!row) return null;
                return (
                  <div className="rounded-md border hairline bg-[var(--surface-raised)] px-3 py-2 text-body-sm shadow-lg" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
                    <p className="mb-1 text-caption text-muted">
                      {series.daily ? formatDate(row.at, locale) : `${time(row.t, range === '7d' ? '24h' : range, locale)} – ${time(row.t + width, range === '7d' ? '24h' : range, locale)}`}
                    </p>
                    {row.avgMs !== null ? (
                      <p className="flex items-center gap-2">
                        <span className="inline-block h-0.5 w-3 rounded-pill" style={{ background: LINE }} aria-hidden />
                        <strong className="tabular text-[var(--text-primary)]">{ms(row.avgMs)}</strong>
                        <span className="text-muted">{t('monitoring.chart.average')}</span>
                      </p>
                    ) : null}
                    {row.maxMs !== null && row.maxMs !== row.avgMs && (
                      <p className="text-caption text-muted">{t('monitoring.chart.slowest', { ms: ms(row.maxMs) })}</p>
                    )}
                    {row.down > 0 && (
                      <p className="mt-1 flex items-center gap-2">
                        <span className="inline-block h-2 w-3 rounded-xs" style={{ background: FAILED, opacity: 0.5 }} aria-hidden />
                        <strong className="tabular text-[var(--text-primary)]">{row.down}</strong>
                        <span className="text-muted">{t('monitoring.chart.failedOf', { n: row.checks })}</span>
                      </p>
                    )}
                    {row.checks === 0 && <p className="text-caption text-muted">{t('monitoring.chart.noneHere')}</p>}
                  </div>
                );
              }}
            />
            <Line
              type="monotone"
              dataKey="avgMs"
              stroke={LINE}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              dot={false}
              activeDot={{ r: 4, fill: LINE, stroke: 'var(--surface-card)', strokeWidth: 2 }}
              connectNulls={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-4 text-caption text-[var(--text-secondary)]">
        <span className="inline-flex items-center gap-2">
          <span className="inline-block h-0.5 w-4 rounded-pill" style={{ background: LINE }} aria-hidden />
          {t(series.daily ? 'monitoring.chart.averageDaily' : 'monitoring.chart.averageBucket')}
        </span>
        {anyFailed && (
          <span className="inline-flex items-center gap-2">
            <span className="inline-block h-3 w-4 rounded-xs" style={{ background: FAILED, opacity: 0.35 }} aria-hidden />
            {t('monitoring.chart.failed')}
          </span>
        )}
      </div>
    </div>
  );
}

const DAY_CLASS: Record<ReturnType<typeof dayTone>, string> = {
  up: 'bg-success',
  some: 'bg-warning',
  down: 'bg-error',
  none: 'bg-[var(--surface-sunken)]',
};

/**
 * The last 90 days, a bar each: green up all day, amber for a little downtime,
 * red for more, grey with no checks. Every bar says its day and figure on hover,
 * and the key says what the colours are — never colour alone.
 */
export function UptimeBars({ days }: { days: UrlSeries['days'] }) {
  const { t, locale } = useI18n();
  const checked = days.filter((d) => d.uptime !== null);
  const overall = checked.length
    ? Math.floor((100 * checked.reduce((sum, d) => sum + (d.uptime ?? 0) * d.checks, 0)) / checked.reduce((sum, d) => sum + d.checks, 0)) / 100
    : null;
  const label = (d: UrlSeries['days'][number]) =>
    d.uptime === null
      ? `${formatDate(d.day, locale)} · ${t('monitoring.chart.noData')}`
      : `${formatDate(d.day, locale)} · ${t('monitoring.chart.dayUp', { pct: `${d.uptime}%` })} · ${t('monitoring.chart.dayChecks', { n: formatNumber(d.checks, locale) })}`;

  return (
    <div>
      <div
        role="img"
        aria-label={overall === null ? t('monitoring.chart.noData') : t('monitoring.chart.ninetySummary', { pct: `${overall}%` })}
        className="flex h-8 items-stretch gap-[2px]"
        dir="ltr"
      >
        {days.map((d) => (
          <span key={d.day} title={label(d)} className={clsx('min-w-[2px] flex-1 rounded-xs transition-opacity hover:opacity-70', DAY_CLASS[dayTone(d.uptime)])} />
        ))}
      </div>
      <div className="mt-1.5 flex justify-between text-caption text-faint" dir="ltr">
        <span>{formatDate(days[0]?.day, locale)}</span>
        <span>{t('monitoring.chart.today')}</span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-caption text-[var(--text-secondary)]">
        {(['up', 'some', 'down', 'none'] as const).map((tone) => (
          <span key={tone} className="inline-flex items-center gap-1.5">
            <span className={clsx('inline-block h-3 w-3 rounded-xs', DAY_CLASS[tone])} aria-hidden />
            {t(`monitoring.chart.key.${tone}` as TranslationKey)}
          </span>
        ))}
      </div>
    </div>
  );
}
